/**
 * MessageInput Component
 *
 * Text input for sending messages with typing indicator emission,
 * character limit display, reply preview, and keyboard shortcuts.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { Message } from '@/types/messaging';

interface MessageInputProps {
  onSend: (content: string, replyToId?: string) => Promise<void>;
  onTypingStart: () => void;
  onTypingStop: () => void;
  replyTo?: Message | null;
  onCancelReply?: () => void;
  disabled?: boolean;
  isSending?: boolean;
  placeholder?: string;
  maxLength?: number;
}

const MAX_MESSAGE_LENGTH = 5000;

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  onTypingStart,
  onTypingStop,
  replyTo,
  onCancelReply,
  disabled = false,
  isSending = false,
  placeholder = 'Type a message...',
  maxLength = MAX_MESSAGE_LENGTH,
}) => {
  const [content, setContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);

  const characterCount = content.length;
  const isOverLimit = characterCount > maxLength;
  const canSend = content.trim().length > 0 && !isOverLimit && !disabled && !isSending;

  /**
   * Auto-resize textarea based on content
   */
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 200); // Max 200px height
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  /**
   * Handle typing indicator emissions
   */
  const handleTyping = useCallback(() => {
    // Emit typing start if not already typing
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      onTypingStart();
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      onTypingStop();
    }, 2000);
  }, [onTypingStart, onTypingStop]);

  /**
   * Handle content change
   */
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    adjustTextareaHeight();

    if (newContent.trim()) {
      handleTyping();
    }
  };

  /**
   * Handle send message
   */
  const handleSend = async () => {
    if (!canSend) return;

    // Stop typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTypingRef.current) {
      isTypingRef.current = false;
      onTypingStop();
    }

    const messageContent = content.trim();
    const replyToId = replyTo?.id;

    // Clear input immediately for responsiveness
    setContent('');
    if (onCancelReply) onCancelReply();

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      await onSend(messageContent, replyToId);
    } catch (error) {
      console.error('[MessageInput] Failed to send message:', error);
      // Restore content on error
      setContent(messageContent);
    }

    // Focus textarea after sending
    textareaRef.current?.focus();
  };

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter to send (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Shift+Enter for newline is default behavior
  };

  /**
   * Cleanup typing timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTypingRef.current) {
        onTypingStop();
      }
    };
  }, [onTypingStop]);

  /**
   * Focus textarea when reply is set
   */
  useEffect(() => {
    if (replyTo) {
      textareaRef.current?.focus();
    }
  }, [replyTo]);

  /**
   * Adjust textarea height when content changes externally
   */
  useEffect(() => {
    adjustTextareaHeight();
  }, [content, adjustTextareaHeight]);

  return (
    <div className="border-t bg-background p-4">
      {/* Reply preview */}
      {replyTo && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-muted/50 rounded-lg border-l-2 border-primary">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-muted-foreground mb-0.5">
              Replying to {replyTo.sender.firstName || replyTo.sender.username}
            </p>
            <p className="text-sm text-foreground truncate">
              {replyTo.content}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancelReply}
            className="h-7 w-7 p-0 shrink-0"
            aria-label="Cancel reply"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Input area */}
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSending}
            className={`min-h-[44px] max-h-[200px] resize-none pr-16 ${
              isOverLimit ? 'border-destructive focus-visible:ring-destructive' : ''
            }`}
            rows={1}
            aria-label="Message input"
            aria-describedby="character-count"
          />

          {/* Character count */}
          <div
            id="character-count"
            className={`absolute bottom-2 right-3 text-xs ${
              isOverLimit
                ? 'text-destructive font-medium'
                : characterCount > maxLength * 0.9
                ? 'text-amber-500'
                : 'text-muted-foreground'
            }`}
          >
            {characterCount}/{maxLength}
          </div>
        </div>

        {/* Send button */}
        <Button
          onClick={handleSend}
          disabled={!canSend}
          size="icon"
          className="h-11 w-11 shrink-0"
          style={
            canSend
              ? {
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                }
              : undefined
          }
          aria-label="Send message"
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </Button>
      </div>

      {/* Keyboard hint */}
      <p className="text-xs text-muted-foreground mt-2">
        Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Enter</kbd> to send,{' '}
        <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Shift + Enter</kbd> for new line
      </p>
    </div>
  );
};

export default MessageInput;
